import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDateTime, titleCase } from '../../lib/format'
import type { GateResult } from '../../types'
import {
  Alert, Avatar, Badge, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton,
} from '../../components/ui'
import { ShieldIcon, TruckIcon, UserIcon } from '../../components/icons'

export default function SecurityGate() {
  const qc = useQueryClient()
  const [plate, setPlate] = useState('')
  const [idNumber, setIdNumber] = useState('')
  const [searchPlate, setSearchPlate] = useState('')
  const [searchId, setSearchId] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'security', 'gate', searchPlate, searchId],
    queryFn: () => api.get<GateResult>(
      `/ops/security/gate?${[searchPlate && `plate=${encodeURIComponent(searchPlate)}`,
        searchId && `id_number=${encodeURIComponent(searchId)}`].filter(Boolean).join('&')}`,
    ),
  })

  const logVerification = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/ops/security/gate/log', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'security', 'gate'] }),
  })

  function onSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSearchPlate(plate.trim())
    setSearchId(idNumber.trim())
  }

  function logTruck(action: string) {
    if (!data?.truck?.id) return
    logVerification.mutate({ subject_type: 'truck', subject_id: data.truck.id, action })
  }

  function logPerson(action: string) {
    if (!data?.person?.uid) return
    logVerification.mutate({ subject_type: 'user', subject_id: data.person.uid, action })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Gate verification</h1>
          <p className="page-head__sub">Verify trucks, IDs and movements at the gate.</p>
        </div>
      </div>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardBody>
          <form onSubmit={onSearch}>
            <div className="grid grid--2" style={{ gap: 'var(--sp-4)', alignItems: 'flex-end' }}>
              <Input label="Truck plate" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="e.g. ABC 123" />
              <Input label="ID / phone / email" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="National ID, phone or email" />
            </div>
            <div className="row" style={{ marginTop: 'var(--sp-4)' }}>
              <Button type="submit">Search</Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>
      ) : data ? (
        <>
          <div className="grid grid--tiles-3" style={{ marginBottom: 'var(--sp-6)' }}>
            <Stat label="Active trucks" value={data.stats.active_trucks} />
            <Stat label="Active loads" value={data.stats.active_loads} />
            <Stat label="Missing ID docs" value={data.stats.missing_ids} tone={data.stats.missing_ids > 0 ? 'warning' : undefined} />
          </div>

          {logVerification.isError && (
            <Alert tone="danger">
              {logVerification.error instanceof ApiError ? logVerification.error.message : 'Could not record that verification.'}
            </Alert>
          )}

          {(searchPlate || searchId) && (
            <div className="grid grid--2" style={{ gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
              <Card>
                <CardHeader title="Truck" />
                <CardBody>
                  {!data.truck ? (
                    <EmptyState icon={<TruckIcon size={20} />} title="No match" body={searchPlate ? 'No truck found for that plate.' : 'Enter a plate to look up a truck.'} />
                  ) : (
                    <div className="stack">
                      {data.truck_org_approved === false && (
                        <Alert tone="danger">
                          This truck's organisation is <strong>not yet approved</strong> to operate.
                        </Alert>
                      )}
                      <Row label="Truck" value={`${data.truck.name} (${data.truck.plate})`} />
                      <Row label="Driver" value={data.truck.driver_name || '—'} />
                      <Row label="Status" value={
                        <Badge dot tone={data.truck_online?.online ? 'success' : 'neutral'}>
                          {data.truck_online?.online ? 'Online' : 'Offline'}
                        </Badge>
                      } />
                      {(data.truck_loads ?? []).length > 0 && (
                        <div>
                          <div className="field__label" style={{ marginBottom: 4 }}>Recent loads</div>
                          {(data.truck_loads ?? []).map((l) => (
                            <div key={l.id} style={{ fontSize: 'var(--text-sm)', padding: '2px 0' }}>
                              {titleCase(l.mineral_type)} · {l.status}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
                        <Button size="sm" loading={logVerification.isPending} onClick={() => logTruck('checked')}>
                          Verify truck
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Person" />
                <CardBody>
                  {!data.person ? (
                    <EmptyState icon={<UserIcon size={20} />} title="No match" body={searchId ? 'No person found for that ID/phone/email.' : 'Enter an ID, phone or email to look up a person.'} />
                  ) : (
                    <div className="stack">
                      {data.person_org_approved === false && (
                        <Alert tone="danger">
                          This person's organisation is <strong>not yet approved</strong> to operate.
                        </Alert>
                      )}
                      <div className="row" style={{ marginBottom: 'var(--sp-1)' }}>
                        <Avatar
                          photoUrl={data.person.photo_url} name={data.person.full_name} email={data.person.email}
                          size={56}
                        />
                        <div>
                          <div style={{ fontWeight: 620 }}>{data.person.full_name || data.person.email || '—'}</div>
                          {!data.person.photo_url && (
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--warning)' }}>No photo on file — verify by ID document</div>
                          )}
                        </div>
                      </div>
                      <Row label="Role" value={titleCase(data.person.role)} />
                      <Row label="ID documents" value={String((data.person_docs ?? []).length)} />
                      <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
                        <Button size="sm" loading={logVerification.isPending} onClick={() => logPerson('checked')}>
                          Verify person
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader title="Recent verifications" />
            <CardBody flush>
              {data.recent_logs.length === 0 ? (
                <EmptyState icon={<ShieldIcon size={22} />} title="No verifications yet" body="Gate verification records will appear here." />
              ) : (
                <>
                  {/* PWA tap rows - the 4-column table's When column clipped
                      off a phone; desktop keeps the table. */}
                  <div className="mobile-only">
                    {data.recent_logs.map((l) => (
                      <div key={l.id} className="lrow">
                        <div className="lrow__main">
                          <div className="lrow__title">{titleCase(l.subject_type)} · {titleCase(l.action)}</div>
                          <div className="lrow__sub">
                            {[l.verified_by_name, formatDateTime(l.created_at)].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="table-wrap desktop-only">
                    <table className="table">
                      <thead><tr><th>Type</th><th>Action</th><th>Verified by</th><th>When</th></tr></thead>
                      <tbody>
                        {data.recent_logs.map((l) => (
                          <tr key={l.id}>
                            <td>{titleCase(l.subject_type)}</td>
                            <td>{titleCase(l.action)}</td>
                            <td>{l.verified_by_name || '—'}</td>
                            <td>{formatDateTime(l.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </>
      ) : null}
    </>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warning' }) {
  return (
    <Card pad>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{
        fontSize: 'var(--text-xl)', fontWeight: 700, marginTop: 'var(--sp-1)',
        color: tone === 'warning' ? 'var(--warning)' : 'var(--text)',
      }}>
        {value}
      </div>
    </Card>
  )
}
